import { useState } from "react";
import { AlertCircle, KeyRound, Loader2, LogOut, Shield } from "lucide-react";
import AdminLicenseConsole from "../components/compliance/AdminLicenseConsole";
import { useLinkLightAdminAuth } from "../context/LinkLightAdminAuthContext";
import {
  clearLinkLightAdminContext,
  setLinkLightAdminContext,
} from "../services/compliance-api";

export default function LinkLightAdminPage() {
  const { session, isAuthenticated, login, logout } = useLinkLightAdminAuth();
  const [tenantId, setTenantId] = useState("tenant_admin");
  const [licenseKey, setLicenseKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const normalizedTenantId = tenantId.trim();
    const normalizedLicenseKey = licenseKey.trim();
    try {
      const res = await fetch("/compliance-api/api/compliance/auth/license/verify", {
        headers: {
          Authorization: `Bearer ${normalizedLicenseKey}`,
          "X-Tenant-ID": normalizedTenantId,
          "X-Compliance-Role": "ROOT_ADMIN",
        },
      });
      if (!res.ok) {
        setError("Invalid LightLink root admin license key.");
        return;
      }
      login({ tenantId: normalizedTenantId, licenseKey: normalizedLicenseKey });
      setLinkLightAdminContext(normalizedTenantId, normalizedLicenseKey);
    } catch {
      setError("Could not reach the LightLink admin service.");
    } finally {
      setLoading(false);
    }
  }

  function handleLogout() {
    clearLinkLightAdminContext();
    logout();
  }

  if (isAuthenticated && session) {
    setLinkLightAdminContext(session.tenantId, session.licenseKey);
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white">
              <Shield size={20} />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-950">LightLink AI Root Admin</p>
              <p className="text-xs text-slate-500">Client licenses and product operations</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
          >
            <LogOut size={15} />
            Sign out
          </button>
        </header>
        <AdminLicenseConsole />
      </div>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-950 px-4">
      <section className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-8 shadow-2xl">
        <div className="mb-7 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-blue-400/30 bg-blue-500/15">
            <KeyRound className="text-blue-300" size={26} />
          </div>
          <h1 className="text-2xl font-bold text-white">LightLink AI Root Admin</h1>
          <p className="mt-1 text-sm text-slate-400">
            Separate product admin access for client license management.
          </p>
        </div>

        {error && (
          <div className="mb-5 flex items-start gap-2 rounded-lg border border-red-700/50 bg-red-900/30 px-3 py-2 text-sm text-red-200">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            {error}
          </div>
        )}

        <form onSubmit={(e) => void handleLogin(e)} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-300">
              Root Admin Tenant
            </label>
            <input
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="tenant_admin"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-300">
              Root Admin License Key
            </label>
            <input
              value={licenseKey}
              onChange={(e) => setLicenseKey(e.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="LL-ROOT-2026-..."
              type="password"
            />
          </div>
          <button
            type="submit"
            disabled={loading || !tenantId.trim() || !licenseKey.trim()}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-500 disabled:bg-slate-600"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Shield size={16} />}
            Open Root Admin
          </button>
        </form>
      </section>
    </main>
  );
}
